import { isEmptyObject, isPresent, mapValues, toBoolean } from '@fcom/core/utils/utils';
import { Category } from '@fcom/dapi/api/models/category';
import { ServiceCatalogSelections, ServiceSelections, TravelerService } from '@fcom/dapi/interfaces/services.interface';
import { NO_VARIANTS } from '@fcom/dapi/interfaces/cart.interface';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairPassengerServiceSelectionItem } from '@fcom/dapi/api/models/finnair-passenger-service-selection-item';
import { FinnairServiceCatalogV3 } from '@fcom/dapi/api/models/finnair-service-catalog-v-3';
import { NotificationTheme } from '@fcom/ui-components/components/notifications/enums';

import { PassengerCountQuota, PassengerCountQuotaType } from '../interfaces/ancillaries-interface';
import {
  ANCILLARY_BUNDLE_CONSTITUENTS,
  getRelatedCategoriesForCategory,
  isBundle,
  SEAT_BUNDLE_CATEGORIES,
} from './category.utils';
import { asSegmentId, isFlight } from '../../../utils/flight.utils';
import { hasTravelerService } from './ancillary.utils';

export interface FragmentQuota {
  fragmentId: string;
  quota: number;
}

interface QuotaBearingService {
  isService?: boolean;
  quota?: number | null;
  variant: string;
  totalPrice?: { amount: string } | null;
}

interface CatalogQuota {
  total: number;
  quotaBearingVariants: string[];
}

export type QuotaBearingServicesPerTraveler = Record<string, QuotaBearingService[]>;
export type QuotaBearingServiceCatalogServices = Record<string, QuotaBearingServicesPerTraveler>;
export type RemainingQuotaPerFragment = Record<string, number | undefined>;

type QuotaBearingCatalog = Record<string, QuotaBearingServiceCatalogServices>;

type QuotaFreeVariantsPerFragment = Record<string, Partial<Record<Category, string[]>>>;

export interface CategoryQuota {
  remaining: RemainingQuotaPerFragment;
  noneAvailableForBooking: boolean;
  lowest?: LowQuotaSegment;
  status?: Record<string, CategoryQuotaState>;
  exhaustedConstituents?: Record<string, Category[]>;
  quotaFreeVariants?: QuotaFreeVariantsPerFragment;
  usedByTravelers?: Record<string, boolean>;
}

export type CategoryQuotas = Partial<Record<Category, CategoryQuota>>;

const isNotIncluded = ({ includedInTicketType, includedInTierBenefit }: TravelerService): boolean =>
  !includedInTicketType && !includedInTierBenefit;

const quotaUsedBySelection = (
  selection: TravelerService | TravelerService[] | undefined,
  quotaFreeVariants: string[] = []
): number =>
  []
    .concat(selection ?? [])
    .filter(isPresent)
    .filter(isNotIncluded)
    .filter(({ variant }) => !NO_VARIANTS.includes(variant) && !quotaFreeVariants.includes(variant))
    .reduce((units: number, { quantity }: TravelerService) => units + (quantity ?? 1), 0);

const quotaUsedInFragment = (
  selectionsForFragment: Record<string, TravelerService | TravelerService[]> | undefined,
  quotaFreeVariants: string[]
): number =>
  Object.values(selectionsForFragment ?? {}).reduce(
    (units, selection) => units + quotaUsedBySelection(selection, quotaFreeVariants),
    0
  );

const catalogQuotaForFragment = (
  servicesPerTraveler: QuotaBearingServicesPerTraveler | undefined
): CatalogQuota | undefined => {
  const variantQuotas = variantQuotasForFragment(servicesPerTraveler);

  return isEmptyObject(variantQuotas)
    ? undefined
    : { total: Math.min(...Object.values(variantQuotas)), quotaBearingVariants: Object.keys(variantQuotas) };
};

const catalogQuotaPerFragment = (
  category: Category,
  catalog: QuotaBearingCatalog | undefined
): Record<string, CatalogQuota> =>
  Object.entries(catalog?.[category] ?? {}).reduce<Record<string, CatalogQuota>>(
    (quotas, [fragmentId, servicesPerTraveler]) => {
      const catalogQuota = catalogQuotaForFragment(servicesPerTraveler);

      return catalogQuota ? { ...quotas, [asSegmentId(fragmentId)]: catalogQuota } : quotas;
    },
    {}
  );

const unitsSelectedOnFragment = (
  selectionsForCategory: ServiceCatalogSelections<TravelerService | TravelerService[]> | undefined,
  fragmentId: string,
  quotaFreeVariants: string[]
): number =>
  Object.entries(selectionsForCategory ?? {})
    .filter(([selectionFragmentId]) => asSegmentId(selectionFragmentId) === fragmentId)
    .reduce(
      (units, [, selectionsForFragment]) => units + quotaUsedInFragment(selectionsForFragment, quotaFreeVariants),
      0
    );

const quotaConstituentsOf = (category: Category): Category[] =>
  getRelatedCategoriesForCategory(category).filter(hasCategoryLevelQuota);

const selectionConsumesCategoryQuota = (category: Category, selectionCategory: Category): boolean => {
  const constituents = quotaConstituentsOf(category);

  return getRelatedCategoriesForCategory(selectionCategory).some((constituent) => constituents.includes(constituent));
};

const quotaFreeVariantsOf = (servicesPerTraveler: QuotaBearingServicesPerTraveler | undefined): string[] => [
  ...new Set(
    Object.values(servicesPerTraveler ?? {})
      .flat()
      .filter((service) => service?.isService && !isPresent(service.quota))
      .map(({ variant }) => variant)
  ),
];

export const getQuotaFreeVariants = (
  category: Category,
  catalog: QuotaBearingCatalog | undefined
): QuotaFreeVariantsPerFragment | undefined => {
  const perFragment = quotaConstituentsOf(category).reduce<QuotaFreeVariantsPerFragment>(
    (all, constituent) =>
      Object.entries(catalog?.[constituent] ?? {}).reduce((acc, [fragmentId, servicesPerTraveler]) => {
        const variants = quotaFreeVariantsOf(servicesPerTraveler);
        const id = asSegmentId(fragmentId);

        return variants.length ? { ...acc, [id]: { ...acc[id], [constituent]: variants } } : acc;
      }, all),
    {}
  );

  return isEmptyObject(perFragment) ? undefined : perFragment;
};

const quotaFreeVariantsOfSelection = (
  selectionCategory: Category,
  fragmentId: string,
  catalog: QuotaBearingCatalog | undefined
): string[] => getQuotaFreeVariants(selectionCategory, catalog)?.[fragmentId]?.[selectionCategory] ?? [];

export const exhaustedConstituentsPerFragment = (
  category: Category,
  remainingOf: (constituent: Category) => RemainingQuotaPerFragment | undefined
): Record<string, Category[]> | undefined => {
  const exhausted = !isBundle(category)
    ? {}
    : quotaConstituentsOf(category).reduce<Record<string, Category[]>>(
        (perFragment, constituent) =>
          Object.entries(remainingOf(constituent) ?? {})
            .filter(([, left]) => left === 0)
            .reduce(
              (all, [fragmentId]) => ({ ...all, [fragmentId]: [...(all[fragmentId] ?? []), constituent] }),
              perFragment
            ),
        {}
      );

  return isEmptyObject(exhausted) ? undefined : exhausted;
};

export const heldServicesCanBeSwitchedToCategory = (
  category: Category,
  heldServices: TravelerService[] | undefined,
  categoryQuota: CategoryQuota | undefined,
  fragmentId: string
): boolean => {
  const exhaustedConstituents = categoryQuota?.exhaustedConstituents?.[fragmentId];
  const quotaFreeVariants = categoryQuota?.quotaFreeVariants?.[fragmentId];
  const categoriesOfServicesUsingAUnit = (heldServices ?? [])
    .filter(
      ({ parentCategory, ...service }) =>
        !!parentCategory && quotaUsedBySelection(service as TravelerService, quotaFreeVariants?.[parentCategory]) > 0
    )
    .map(({ parentCategory }) => parentCategory);

  if (!exhaustedConstituents?.length) {
    return categoriesOfServicesUsingAUnit.some(
      (heldCategory) =>
        selectionConsumesCategoryQuota(category, heldCategory) && (!isBundle(category) || isBundle(heldCategory))
    );
  }

  const categoriesGivingUnitBack = categoriesOfServicesUsingAUnit.flatMap((heldCategory) =>
    getRelatedCategoriesForCategory(heldCategory)
  );

  return exhaustedConstituents.every((constituent) => categoriesGivingUnitBack.includes(constituent));
};

export const isCategoryQuotaReached = (
  categoryQuota: CategoryQuota | undefined,
  fragmentId: string,
  catalogQuotaReached: boolean
): boolean => {
  const remaining = categoryQuota?.remaining?.[fragmentId];

  return isPresent(remaining) ? remaining === 0 : catalogQuotaReached;
};

export const bundleQuotaBlocksPassenger = ({
  quotaReached,
  category,
  heldServices,
  categoryQuota,
  fragmentId,
}: {
  quotaReached: boolean;
  category: Category;
  heldServices: TravelerService[] | undefined;
  categoryQuota: CategoryQuota | undefined;
  fragmentId: string;
}): boolean => quotaReached && !heldServicesCanBeSwitchedToCategory(category, heldServices, categoryQuota, fragmentId);

const categoryQuotaUsed = (
  category: Category,
  fragmentId: string,
  selections: ServiceSelections,
  catalog: QuotaBearingCatalog | undefined
): number => {
  return Object.entries(selections as Record<string, ServiceCatalogSelections<TravelerService | TravelerService[]>>)
    .filter(([selectionCategory]) => selectionConsumesCategoryQuota(category, selectionCategory as Category))
    .reduce(
      (used, [selectionCategory, selectionsForCategory]) =>
        used +
        unitsSelectedOnFragment(
          selectionsForCategory,
          fragmentId,
          quotaFreeVariantsOfSelection(selectionCategory as Category, fragmentId, catalog)
        ),
      0
    );
};

export const isFreeService = ({ totalPrice }: QuotaBearingService): boolean =>
  isPresent(totalPrice) && parseFloat(totalPrice.amount) === 0;

const withoutFreeServices = (
  servicesPerTraveler: QuotaBearingServicesPerTraveler | undefined
): QuotaBearingServicesPerTraveler =>
  mapValues(servicesPerTraveler ?? {}, (services: QuotaBearingService[]) =>
    (services ?? []).filter((service) => isPresent(service) && !isFreeService(service))
  );

export const withoutFreeServicesPerFragment = (
  services: QuotaBearingServiceCatalogServices | undefined
): QuotaBearingServiceCatalogServices =>
  mapValues(services ?? {}, (servicesPerTraveler: QuotaBearingServicesPerTraveler) =>
    withoutFreeServices(servicesPerTraveler)
  );

export type VariantUnits = Record<string, number>;
export type VariantUnitsPerTraveler = Record<string, VariantUnits>;
export type VariantUnitsPerFragment = Record<string, VariantUnitsPerTraveler>;

export const SUFFICIENT_QUOTA_SENTINEL = 9;

// The API caps quota at 9, where 9 stands for "9 or more", so only values below it are counts we know exactly.
export const isKnownQuota = (quota: number | null | undefined): quota is number =>
  isPresent(quota) && quota < SUFFICIENT_QUOTA_SENTINEL;

export const isLowQuota = (remaining: number | null | undefined): boolean => isKnownQuota(remaining) && remaining > 0;

export const CATEGORY_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.MEAL, Category.PRIORITY];
export const VARIANT_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.SPORT];

export const hasCategoryLevelQuota = (category: Category): boolean =>
  CATEGORY_LEVEL_QUOTA_CATEGORIES.includes(category);

export const sharesCategoryLevelQuota = (category: Category): boolean =>
  getRelatedCategoriesForCategory(category).some((c) => hasCategoryLevelQuota(c));

export const hasVariantLevelQuota = (category: Category): boolean => VARIANT_LEVEL_QUOTA_CATEGORIES.includes(category);

export const variantQuotasForFragment = (services: QuotaBearingServicesPerTraveler | undefined): VariantUnits =>
  Object.values(services ?? {})
    .flat()
    .filter(isPresent)
    .filter(({ isService }) => isService)
    .reduce<VariantUnits>((quotas, { variant, quota }) => {
      if (!isKnownQuota(quota)) {
        return quotas;
      }

      return { ...quotas, [variant]: isPresent(quotas[variant]) ? Math.min(quotas[variant], quota) : quota };
    }, {});

export const variantUnitsAcrossTravelers = (unitsPerTraveler: VariantUnitsPerTraveler | undefined): VariantUnits =>
  Object.values(unitsPerTraveler ?? {}).reduce<VariantUnits>(
    (totals, unitsForTraveler) =>
      Object.entries(unitsForTraveler ?? {}).reduce(
        (all, [variant, units]) => ({ ...all, [variant]: (all[variant] ?? 0) + units }),
        totals
      ),
    {}
  );

export const remainingVariantQuota = (pool: number, selectedUnits: number, unpaidUnitsAtLoad: number): number =>
  Math.max(pool - (selectedUnits - unpaidUnitsAtLoad), 0);

export interface QuotaUsage {
  services?: QuotaBearingServiceCatalogServices;
  selectedUnits?: VariantUnitsPerFragment;
  unpaidUnitsAtLoad?: VariantUnitsPerFragment;
}

export const remainingVariantQuotasPerFragment = ({
  services,
  selectedUnits,
  unpaidUnitsAtLoad,
}: QuotaUsage): Record<string, VariantUnits> => {
  return Object.entries(services ?? {}).reduce<Record<string, VariantUnits>>(
    (remaining, [fragmentId, servicesPerTraveler]) => {
      const pools = variantQuotasForFragment(servicesPerTraveler);

      if (isEmptyObject(pools)) {
        return remaining;
      }

      const selected = variantUnitsAcrossTravelers(selectedUnits?.[fragmentId]);
      const unpaid = variantUnitsAcrossTravelers(unpaidUnitsAtLoad?.[fragmentId]);

      return {
        ...remaining,
        [fragmentId]: mapValues(pools, (pool: number, variant: string) =>
          remainingVariantQuota(pool, selected[variant] ?? 0, unpaid[variant] ?? 0)
        ),
      };
    },
    {}
  );
};

export const unitsAcrossTravelersForVariants = (
  unitsPerTraveler: VariantUnitsPerTraveler | undefined,
  variants: string[]
): number => {
  const totals = variantUnitsAcrossTravelers(unitsPerTraveler);

  return variants.reduce((sum, variant) => sum + (totals[variant] ?? 0), 0);
};

export const remainingCategoryQuotasPerFragment = ({
  services,
  selectedUnits,
  unpaidUnitsAtLoad,
}: QuotaUsage): RemainingQuotaPerFragment =>
  Object.entries(services ?? {}).reduce<RemainingQuotaPerFragment>((remaining, [fragmentId, servicesPerTraveler]) => {
    const catalogQuota = catalogQuotaForFragment(servicesPerTraveler);

    if (!catalogQuota) {
      return remaining;
    }

    const { total, quotaBearingVariants } = catalogQuota;
    const selected = unitsAcrossTravelersForVariants(selectedUnits?.[fragmentId], quotaBearingVariants);
    const unpaid = unitsAcrossTravelersForVariants(unpaidUnitsAtLoad?.[fragmentId], quotaBearingVariants);

    return { ...remaining, [fragmentId]: remainingVariantQuota(total, selected, unpaid) };
  }, {});

export const lowestQuotaAcrossServiceVariants = (
  services: QuotaBearingServicesPerTraveler | undefined
): number | undefined => {
  const quotas = Object.values(services ?? {})
    .flat()
    .filter(Boolean)
    .filter(({ isService }) => isService)
    .map(({ quota }) => quota)
    .filter(isPresent);

  return quotas.length ? Math.min(...quotas) : undefined;
};

export const lowestQuotaAcrossFlights = (
  services: QuotaBearingServiceCatalogServices | undefined,
  initeraryFlightIdsInTravelOrder: string[]
): FragmentQuota | undefined =>
  initeraryFlightIdsInTravelOrder.reduce<FragmentQuota | undefined>((lowestFragment, flightId) => {
    const quota = lowestQuotaAcrossServiceVariants(services?.[flightId]);

    if (!isPresent(quota)) {
      return lowestFragment;
    }

    const currentFragmentQuota: FragmentQuota = { fragmentId: flightId, quota };

    if (!lowestFragment) {
      return currentFragmentQuota;
    }

    return currentFragmentQuota.quota < lowestFragment.quota ? currentFragmentQuota : lowestFragment;
  }, undefined);

export interface LowQuotaSegment {
  count: number;
  segment: string;
}

interface LowestLowQuotaSegmentArguments {
  services: QuotaBearingServiceCatalogServices | undefined;
  quotaPerFragment?: RemainingQuotaPerFragment;
  bounds: FinnairBoundItem[] | undefined;
  purchasesDonePerFragment?: Record<string, boolean>;
  unpaidUnitsAtLoad?: VariantUnitsPerFragment;
  nowMillis?: number;
}

const lowestQuotaSegmentOf = (
  flights: FinnairItineraryItemFlight[],
  remaining: RemainingQuotaPerFragment
): LowQuotaSegment | undefined => {
  const lowestQuotaFlight = flights
    .filter((flight) => isLowQuota(remaining[flight.id]))
    .sort((aFlight, bFlight) => Date.parse(aFlight.departure.dateTime) - Date.parse(bFlight.departure.dateTime))
    .reduce<FinnairItineraryItemFlight | undefined>(
      (currentLowest, flight) =>
        !currentLowest || remaining[flight.id] < remaining[currentLowest.id] ? flight : currentLowest,
      undefined
    );

  if (!lowestQuotaFlight) {
    return undefined;
  }

  const { departure, arrival } = lowestQuotaFlight;

  return { count: remaining[lowestQuotaFlight.id], segment: `${departure.locationCode}-${arrival.locationCode}` };
};

export const lowestQuotaSegment = (
  bounds: FinnairBoundItem[] | undefined,
  remaining: RemainingQuotaPerFragment
): LowQuotaSegment | undefined =>
  lowestQuotaSegmentOf(
    (bounds ?? []).flatMap((bound) => bound.itinerary.filter(isFlight)),
    remaining
  );

export const lowestLowQuotaSegment = ({
  services,
  quotaPerFragment,
  bounds,
  purchasesDonePerFragment = {},
  unpaidUnitsAtLoad,
  nowMillis = Date.now(),
}: LowestLowQuotaSegmentArguments): LowQuotaSegment | undefined => {
  const remainingQuotaPerFragment =
    quotaPerFragment ??
    remainingCategoryQuotasPerFragment({
      services: withoutFreeServicesPerFragment(services),
      unpaidUnitsAtLoad,
    });

  const upcomingFlights = (bounds ?? [])
    .flatMap((bound) => bound.itinerary.filter(isFlight))
    .filter((flight) => Date.parse(flight.departure.dateTime) > nowMillis)
    .filter((flight) => !purchasesDonePerFragment[flight.id]);

  return lowestQuotaSegmentOf(upcomingFlights, remainingQuotaPerFragment);
};

export type SelectionQuota = Omit<CategoryQuota, 'noneAvailableForBooking'>;

export const toCategoryQuota = ({
  selectionQuota,
  catalogNoneLeft,
  unpaidCount,
}: {
  selectionQuota: SelectionQuota | undefined;
  catalogNoneLeft: boolean;
  unpaidCount: number;
}): CategoryQuota | undefined => {
  if (!selectionQuota && !catalogNoneLeft) {
    return undefined;
  }

  const hasUnpaid = unpaidCount > 0;

  return {
    remaining: {},
    ...selectionQuota,
    noneAvailableForBooking: catalogNoneLeft && !hasUnpaid,
    lowest: hasUnpaid ? undefined : selectionQuota?.lowest,
  };
};

export const withoutSelectionQuota = (quota: CategoryQuota | undefined): CategoryQuota | undefined =>
  quota && { remaining: {}, noneAvailableForBooking: quota.noneAvailableForBooking };

export interface CartQuotaInput {
  catalog: QuotaBearingCatalog | undefined;
  sellableCatalog: QuotaBearingCatalog | undefined;
  selections: ServiceSelections | undefined;
  bounds: FinnairBoundItem[] | undefined;
  passengerCount: number;
}

export const getCartSelectionQuota = (
  category: Category,
  { catalog, sellableCatalog, selections, bounds, passengerCount }: CartQuotaInput
): SelectionQuota => {
  const remaining = remainingSharedQuotaPerFragment(category, catalog, selections);
  const remainingSellable = remainingSharedQuotaPerFragment(category, catalog, selections, sellableCatalog);

  const exhaustedConstituents = exhaustedConstituentsPerFragment(category, (constituent) =>
    remainingSharedQuotaPerFragment(constituent, catalog, selections)
  );
  const quotaFreeVariants = getQuotaFreeVariants(category, catalog);
  const usedByTravelers = mapValues(
    remaining,
    (_left, fragmentId) => categoryQuotaUsed(category, fragmentId, selections ?? {}, catalog) > 0
  );

  return {
    remaining,
    usedByTravelers,
    lowest: lowestQuotaSegment(bounds, remainingSellable),
    status: categoryQuotaStatusPerFragment({
      category,
      remaining,
      services: catalog?.[category],
      selections,
      passengerCount,
    }),
    ...(exhaustedConstituents && { exhaustedConstituents }),
    ...(quotaFreeVariants && { quotaFreeVariants }),
  };
};

/** The categories whose "none left" copy comes from the CMS. The others share the generic copy. */
const CMS_NO_QUOTA_LEFT_COPY_CATEGORIES: Category[] = [Category.PET, Category.PRIORITY];

export const getNoQuotaLeftMessageKey = (category: Category): string =>
  CMS_NO_QUOTA_LEFT_COPY_CATEGORIES.includes(category)
    ? `services.${category}.noQuotaLeft`
    : 'ancillaries.quota.noQuotaLeft';

export const getCategoryLevelQuotaCount = (
  serviceCatalog: FinnairServiceCatalogV3 | undefined,
  category: Category,
  bound: FinnairBoundItem,
  fragmentIdsToConsider?: string[]
): number | undefined => {
  if (!hasCategoryLevelQuota(category) || !serviceCatalog) {
    return undefined;
  }

  const catalogForCategory = serviceCatalog.categories.find((item) => item.category === category);
  if (!catalogForCategory) {
    return 0;
  }

  const itineraryFlightId = bound.itinerary.filter(isFlight).map((flight) => flight.id);
  const sellableFlightIds = itineraryFlightId.filter((id) => Boolean(catalogForCategory.services?.[id]));
  if (sellableFlightIds.length === 0) {
    return 0;
  }

  const flightIdsToCount = fragmentIdsToConsider
    ? sellableFlightIds.filter((id) => fragmentIdsToConsider.includes(id))
    : sellableFlightIds;

  if (flightIdsToCount.length === 0) {
    return undefined;
  }

  const lowestQuota = lowestQuotaAcrossFlights(
    withoutFreeServicesPerFragment(catalogForCategory.services),
    flightIdsToCount
  )?.quota;

  return lowestQuota ?? SUFFICIENT_QUOTA_SENTINEL;
};

export enum CategoryQuotaStateKind {
  NONE = 'NONE',
  ENOUGH_FOR_ALL = 'ENOUGH_FOR_ALL',
  ALL_SELECTED = 'ALL_SELECTED',
  SHORT_FOR_GROUP = 'SHORT_FOR_GROUP',
  SHORT_FOR_GROUP_NONE_LEFT = 'SHORT_FOR_GROUP_NONE_LEFT',
}

export type CategoryQuotaState =
  | { kind: CategoryQuotaStateKind.NONE }
  | { kind: CategoryQuotaStateKind.ENOUGH_FOR_ALL; remaining: number }
  | { kind: CategoryQuotaStateKind.ALL_SELECTED }
  | { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP; remaining: number; passengerCount: number }
  | { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT };

export interface QuotaNotification {
  titleKey?: string;
  descriptionKey: string;
  params: Record<string, number>;
  theme: NotificationTheme;
}

interface QuotaStateCopy {
  title: string;
  description: string;
}

export interface CategoryQuotaCopy {
  enoughForAll: QuotaStateCopy;
  allSelected: QuotaStateCopy;
  shortForGroup: QuotaStateCopy;
  shortForGroupNoneLeft: { description: string };
  takenByOtherPassenger: string;
  quotaReached?: string;
}

const pluralizedKey = (key: string, count: number): string => `${key}.${count === 1 ? 'singular' : 'plural'}`;

const categoryQuotaCopy = (root: string, quotaReached?: string): CategoryQuotaCopy => ({
  enoughForAll: { title: `${root}.enoughForAll.title`, description: `${root}.enoughForAll.description` },
  allSelected: { title: `${root}.allSelected.title`, description: `${root}.allSelected.description` },
  shortForGroup: { title: `${root}.shortForGroup.title`, description: `${root}.shortForGroup.description` },
  shortForGroupNoneLeft: { description: `${root}.shortForGroup.noneLeft` },
  takenByOtherPassenger: `${root}.takenByOtherPassenger`,
  quotaReached,
});

const BUNDLE_QUOTA_COPY_ROOT = 'ancillaries.bundle.quota';
const BUNDLE_QUOTA_REACHED_KEY = 'ancillaries.bundle.quotaReached';

const BUNDLES_WITH_OWN_QUOTA_COPY: Category[] = [
  Category.SEAT_MEAL,
  Category.WIFI_MEAL,
  Category.SEAT_PRIORITY,
  Category.LOUNGE_PRIORITY,
];

const bundleQuotaCopy = (category: Category): CategoryQuotaCopy => {
  if (!BUNDLES_WITH_OWN_QUOTA_COPY.includes(category)) {
    return categoryQuotaCopy(BUNDLE_QUOTA_COPY_ROOT, BUNDLE_QUOTA_REACHED_KEY);
  }

  const root = `ancillaries.${category}.quota`;

  return categoryQuotaCopy(root, `${root}.quotaReached`);
};

export const CATEGORY_QUOTA_COPY: Partial<Record<Category, CategoryQuotaCopy>> = {
  [Category.MEAL]: categoryQuotaCopy('ancillaries.meals.quota'),
  [Category.PRIORITY]: categoryQuotaCopy('ancillaries.priority.quota'),
  ...Object.fromEntries(
    [...Object.keys(ANCILLARY_BUNDLE_CONSTITUENTS), ...SEAT_BUNDLE_CATEGORIES].map((category) => [
      category,
      bundleQuotaCopy(category as Category),
    ])
  ),
};

/**
 * What tells a passenger who is blocked that the quota went to someone else. When the quota that ran out is that of a
 * constituent, as the meal of a wifi + meal combo, the text is the constituent's: the meal is what went, whichever
 * product it went in. Otherwise the category's own text, for a bundle that is limited by its own stock.
 */
export const getTakenByOtherPassengerKey = (
  category: Category,
  categoryQuota: CategoryQuota | undefined,
  fragmentId: string
): string | undefined => {
  if (categoryQuota?.usedByTravelers?.[fragmentId] === false) {
    return undefined;
  }

  const exhaustedConstituents = categoryQuota?.exhaustedConstituents?.[fragmentId];

  return (
    (exhaustedConstituents?.length === 1
      ? CATEGORY_QUOTA_COPY[exhaustedConstituents[0]]?.takenByOtherPassenger
      : undefined) ?? CATEGORY_QUOTA_COPY[category]?.takenByOtherPassenger
  );
};

export const getQuotaReachedKey = (category: Category): string =>
  CATEGORY_QUOTA_COPY[category]?.quotaReached ?? BUNDLE_QUOTA_REACHED_KEY;

export const VARIANT_QUOTA_COPY_FALLBACK = 'default';

export type VariantQuotaCopyState = 'exhausted' | 'takenByOtherPassenger';

const VARIANT_QUOTA_COPY_ROOT: Partial<Record<Category, string>> = {
  [Category.SPORT]: 'ancillaries.sport.quota',
};

export const variantQuotaCopyKeys = (category: Category, variant: string, state: VariantQuotaCopyState): string[] => {
  const root = VARIANT_QUOTA_COPY_ROOT[category];

  if (!root) {
    return [];
  }

  return [`${root}.${state}.${variant}`, `${root}.${state}.${VARIANT_QUOTA_COPY_FALLBACK}`];
};

export const getCategoryQuotaState = (
  remaining: number | undefined,
  travelersWithoutSelection: number,
  passengerCount: number
): CategoryQuotaState => {
  if (!isPresent(remaining)) {
    return { kind: CategoryQuotaStateKind.NONE };
  }

  const shortForGroup = remaining < travelersWithoutSelection;

  if (remaining === 0) {
    return shortForGroup
      ? { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT }
      : { kind: CategoryQuotaStateKind.ALL_SELECTED };
  }

  if (shortForGroup) {
    return { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP, remaining, passengerCount };
  }

  return isLowQuota(remaining)
    ? { kind: CategoryQuotaStateKind.ENOUGH_FOR_ALL, remaining }
    : { kind: CategoryQuotaStateKind.NONE };
};

/**
 * The quota state of a category on each fragment it has quota for: what is left, set against the travelers who still
 * need one. Whoever holds the category, a combo that contains it, or has already bought it, is not left needing one.
 * Nothing is said about a fragment where every quota-bearing service is free.
 */
export const categoryQuotaStatusPerFragment = ({
  category,
  remaining,
  services,
  selections,
  passengerCount,
  purchasedByPerFragment = {},
}: {
  category: Category;
  remaining: RemainingQuotaPerFragment;
  services?: QuotaBearingServiceCatalogServices;
  selections: ServiceSelections | undefined;
  passengerCount: number;
  /** The travelers who have already bought the category, per fragment. Only orders have them. */
  purchasedByPerFragment?: Record<string, string[]>;
}): Record<string, CategoryQuotaState> =>
  mapValues(remaining, (left, fragmentId) => {
    if (services && allQuotaBearingServicesFree(services[fragmentId])) {
      return { kind: CategoryQuotaStateKind.NONE };
    }

    const passengersWithSelections = Object.entries<TravelerService | TravelerService[]>(
      selections?.[category as keyof ServiceSelections]?.[fragmentId] ?? {}
    )
      .filter(([, selection]) => hasTravelerService(selection))
      .map(([travelerId]) => travelerId);

    const passengersWithExistingSelection = new Set([
      ...passengersWithSelections,
      ...passengersWithBundleSelectionForCategory(category, fragmentId, selections),
      ...(purchasedByPerFragment[fragmentId] ?? []),
    ]);

    return getCategoryQuotaState(
      left,
      Math.max(passengerCount - passengersWithExistingSelection.size, 0),
      passengerCount
    );
  });

export const getCategoryQuotaNotification = (
  state: CategoryQuotaState | undefined,
  category: Category
): QuotaNotification | undefined => {
  if (!state) {
    return undefined;
  }

  const copy = CATEGORY_QUOTA_COPY[category];

  if (!copy) {
    return undefined;
  }

  switch (state.kind) {
    case CategoryQuotaStateKind.ENOUGH_FOR_ALL:
      return {
        titleKey: pluralizedKey(copy.enoughForAll.title, state.remaining),
        descriptionKey: copy.enoughForAll.description,
        params: { amount: state.remaining },
        theme: NotificationTheme.INFO,
      };
    case CategoryQuotaStateKind.ALL_SELECTED:
      return {
        titleKey: copy.allSelected.title,
        descriptionKey: copy.allSelected.description,
        params: {},
        theme: NotificationTheme.INFO,
      };
    case CategoryQuotaStateKind.SHORT_FOR_GROUP:
      return {
        titleKey: pluralizedKey(copy.shortForGroup.title, state.remaining),
        descriptionKey: copy.shortForGroup.description,
        params: { amount: state.remaining, passengerCount: state.passengerCount },
        theme: NotificationTheme.WARNING,
      };
    case CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT:
      return {
        descriptionKey: copy.shortForGroupNoneLeft.description,
        params: {},
        theme: NotificationTheme.WARNING,
      };
    default:
      return undefined;
  }
};

export type VariantSelectionValues = Record<string, string | boolean | number>;

export const countOwnUnitsOfVariants = (selection: VariantSelectionValues, variants: string[]): number => {
  if (typeof selection?.['radio'] === 'string') {
    return variants.includes(selection['radio']) ? 1 : 0;
  }

  return variants.reduce((units, variant) => {
    const value = selection?.[variant];

    if (typeof value === 'number') {
      return units + value;
    }

    const selections = toBoolean(value) ? 1 : 0;
    return units + selections;
  }, 0);
};

export const quotaBearingVariantsOf = (services: QuotaBearingService[]): string[] =>
  services.filter((service) => service.isService && isKnownQuota(service.quota)).map(({ variant }) => variant);

export const allQuotaBearingServicesFree = (
  servicesPerTraveler: QuotaBearingServicesPerTraveler | undefined
): boolean =>
  Object.values(servicesPerTraveler ?? {})
    .flat()
    .filter((service) => isPresent(service) && service.isService && isKnownQuota(service.quota))
    .every(isFreeService);

export const paidUnitsOfVariants = (
  purchasedServices: Array<Pick<FinnairPassengerServiceSelectionItem, 'variant' | 'quantity'>>,
  variants: string[]
): number =>
  purchasedServices
    .filter(({ variant }) => variants.includes(variant))
    .reduce((units, { quantity }) => units + (quantity ?? 1), 0);

const getPassengerCountQuotaType = ({
  isSelected,
  availableQuota,
  passengersWithSelection,
  travelerHasSelection,
}: {
  isSelected: boolean;
  availableQuota: number;
  passengersWithSelection: number;
  travelerHasSelection: boolean;
}): PassengerCountQuotaType => {
  if (isSelected) {
    return PassengerCountQuotaType.SELECTED;
  }

  if (availableQuota === 0) {
    return PassengerCountQuotaType.NO;
  }

  const isQuotaToppedByCustomer =
    passengersWithSelection > 0 && passengersWithSelection >= availableQuota && !travelerHasSelection;

  if (isQuotaToppedByCustomer) {
    return PassengerCountQuotaType.TOPPED;
  }

  return PassengerCountQuotaType.FREE;
};

export const calculatePassengerCountQuota = (
  passengersWithSelection: number,
  service: { quota?: number | null },
  isSelected: boolean,
  passengersWithSelectionAtInit: number,
  travelerHasSelection: boolean
): PassengerCountQuota => {
  const quota = service.quota === null ? undefined : service.quota;
  const availableQuota = quota + passengersWithSelectionAtInit;

  return {
    type: getPassengerCountQuotaType({ isSelected, availableQuota, passengersWithSelection, travelerHasSelection }),
    count: quota !== undefined ? availableQuota - passengersWithSelection : undefined,
  };
};

/** The flights, by segment id, where the catalog offers the category. */
const offeredFragmentIds = (category: Category, catalog: QuotaBearingCatalog | undefined): string[] =>
  Object.entries(catalog?.[category] ?? {})
    .filter(([, servicesPerTraveler]) =>
      Object.values(servicesPerTraveler ?? {}).some((services) => services?.some((service) => service?.isService))
    )
    .map(([fragmentId]) => asSegmentId(fragmentId));

/**
 * What is left of the quota of a category on each flight where it is offered: the catalog quota, less what the
 * selections of every category that shares it use. The backend owns the catalog quota.
 */
export const remainingSharedQuotaPerFragment = (
  category: Category,
  catalog: QuotaBearingCatalog | undefined,
  selections: ServiceSelections | undefined,
  sellableCatalog: QuotaBearingCatalog | undefined = catalog
): RemainingQuotaPerFragment => {
  const catalogQuotas = catalogQuotaPerFragment(category, sellableCatalog);

  return Object.fromEntries(
    offeredFragmentIds(category, sellableCatalog).map((fragmentId) => {
      const limit = catalogQuotas[fragmentId]?.total;
      const used = categoryQuotaUsed(category, fragmentId, selections ?? {}, catalog);

      return [fragmentId, isPresent(limit) ? Math.max(limit - used, 0) : undefined];
    })
  );
};

/**
 * Passengers who use the quota of the category through a bundle that contains it, e.g. whoever holds a wifi + meal
 * combo has a meal, so is not left needing one.
 */
export const passengersWithBundleSelectionForCategory = (
  category: Category,
  fragmentId: string,
  selections: ServiceSelections | undefined
): Set<string> =>
  new Set(
    Object.entries(selections ?? {})
      .filter(
        ([selectionCategory]) =>
          selectionCategory !== category &&
          getRelatedCategoriesForCategory(selectionCategory as Category).includes(category)
      )
      .flatMap(([, selectionsForCategory]) =>
        Object.entries<TravelerService | TravelerService[]>(selectionsForCategory?.[asSegmentId(fragmentId)] ?? {})
          .filter(([, selection]) => quotaUsedBySelection(selection) > 0)
          .map(([travelerId]) => travelerId)
      )
  );
